const MERCHANT_ORDER_ID_KEY = "merchantOrderId";
const PHONEPE_RESULT_KEY = "phonePePaymentResult";
const DONATION_FORM_DRAFT_KEY = "donationFormDraft";
const MEMBERSHIP_APPLY_DRAFT_KEY = "membershipApplyFormDraft";
const MEMBERSHIP_RENEW_DRAFT_KEY = "membershipRenewFormDraft";
const TOURNAMENT_REGISTRATION_DRAFT_KEY = "tournamentRegistrationFormDraft";
const PAYMENT_RETURN_PATH_KEY = "paymentReturnPath";
const CONSUMED_ORDER_IDS_KEY = "phonePeConsumedOrderIds";

export function isPhonePeCompleted(statusObject) {
  return String(statusObject?.state || "").toUpperCase() === "COMPLETED";
}

function getConsumedOrderIds() {
  try {
    const raw = sessionStorage.getItem(CONSUMED_ORDER_IDS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
  } catch {
    return new Set();
  }
}

export function markPhonePeOrderConsumed(orderId) {
  if (!orderId) return;
  const ids = getConsumedOrderIds();
  ids.add(String(orderId));
  sessionStorage.setItem(CONSUMED_ORDER_IDS_KEY, JSON.stringify([...ids]));
}

export function isPhonePeOrderConsumed(orderId) {
  if (!orderId) return false;
  return getConsumedOrderIds().has(String(orderId));
}

/** Completed PhonePe result that has not yet been saved as a donation/payment row. */
export function isActivePhonePePayment(statusObject) {
  if (!isPhonePeCompleted(statusObject)) return false;
  return !isPhonePeOrderConsumed(statusObject?.orderId);
}

export function clearOnlinePaymentSession() {
  clearMerchantOrderId();
  clearPhonePePaymentResult();
  clearDonationFormDraft();
}

export function getMerchantOrderIdFromUrlOrSession() {
  const params = new URLSearchParams(window.location.search);
  return params.get("merchantOrderId") || sessionStorage.getItem(MERCHANT_ORDER_ID_KEY);
}

export function setMerchantOrderId(orderId) {
  sessionStorage.setItem(MERCHANT_ORDER_ID_KEY, orderId);
}

export function clearMerchantOrderId() {
  sessionStorage.removeItem(MERCHANT_ORDER_ID_KEY);
}

export function setPhonePePaymentResult(statusObject) {
  sessionStorage.setItem(PHONEPE_RESULT_KEY, JSON.stringify(statusObject));
}

export function getPhonePePaymentResult() {
  const raw = sessionStorage.getItem(PHONEPE_RESULT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPhonePePaymentResult() {
  sessionStorage.removeItem(PHONEPE_RESULT_KEY);
}

export function setDonationFormDraft(formData) {
  sessionStorage.setItem(DONATION_FORM_DRAFT_KEY, JSON.stringify(formData));
}

export function getDonationFormDraft() {
  const raw = sessionStorage.getItem(DONATION_FORM_DRAFT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearDonationFormDraft() {
  sessionStorage.removeItem(DONATION_FORM_DRAFT_KEY);
}

export function setPaymentReturnPath(path) {
  if (path) sessionStorage.setItem(PAYMENT_RETURN_PATH_KEY, String(path));
}

/** Current app route (path + query + hash) — use when starting PhonePe checkout. */
export function capturePaymentReturnPath() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search || ""}${hash || ""}`;
}

export function getPaymentReturnPath() {
  return sessionStorage.getItem(PAYMENT_RETURN_PATH_KEY) || "";
}

export function clearPaymentReturnPath() {
  sessionStorage.removeItem(PAYMENT_RETURN_PATH_KEY);
}

/** Persist order id and where to return after PhonePe status page. */
export function beginPhonePeRedirect(merchantOrderId, returnPath) {
  setPaymentReturnPath(returnPath || capturePaymentReturnPath());
  setMerchantOrderId(merchantOrderId);
}

export function setMembershipApplyFormDraft(formData) {
  sessionStorage.setItem(MEMBERSHIP_APPLY_DRAFT_KEY, JSON.stringify(formData));
}

export function getMembershipApplyFormDraft() {
  const raw = sessionStorage.getItem(MEMBERSHIP_APPLY_DRAFT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearMembershipApplyFormDraft() {
  sessionStorage.removeItem(MEMBERSHIP_APPLY_DRAFT_KEY);
}

export function setMembershipRenewFormDraft(formData) {
  sessionStorage.setItem(MEMBERSHIP_RENEW_DRAFT_KEY, JSON.stringify(formData));
}

export function getMembershipRenewFormDraft() {
  const raw = sessionStorage.getItem(MEMBERSHIP_RENEW_DRAFT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearMembershipRenewFormDraft() {
  sessionStorage.removeItem(MEMBERSHIP_RENEW_DRAFT_KEY);
}

export function clearMembershipPaymentSession() {
  clearMembershipApplyFormDraft();
  clearMembershipRenewFormDraft();
}

export function setTournamentRegistrationFormDraft(formData) {
  sessionStorage.setItem(TOURNAMENT_REGISTRATION_DRAFT_KEY, JSON.stringify(formData));
}

export function getTournamentRegistrationFormDraft() {
  const raw = sessionStorage.getItem(TOURNAMENT_REGISTRATION_DRAFT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearTournamentRegistrationFormDraft() {
  sessionStorage.removeItem(TOURNAMENT_REGISTRATION_DRAFT_KEY);
}

export function clearTournamentPaymentSession() {
  clearTournamentRegistrationFormDraft();
}
